import { useEffect } from "react";
import { getSubscriptionStatus } from "@/api/billing";
import { useAuth } from "@/hooks/use-auth";
import { formatDate, translate } from "@/i18n";
import { settingsBillingMessages } from "@/i18n/messages/settings-billing";
import { useNotificationStore } from "@/stores/notification-store";
import { newCheckoutAmount } from "./billing-plan-content";
import { watchBillingRefresh } from "./billing-refresh";
import { isTauri } from "@/lib/is-tauri";
import { openWebBillingSettings, showUpgradeToast } from "@/lib/upgrade-toast";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  onboardingSystemNoticesBlocked,
  registerOnboardingTrialRefresh,
  hasDeferredSystemNotice,
  discardDeferredTrialNotices,
} from "@/lib/onboarding-system-notices";
import { getWindowRoleSync, useWindowRole } from "@/desktop/use-window-role";

const STATUS_REFRESH_MS = 60_000;

/** Read-only lifecycle notices. Viewing the app never starts a trial. */
export function BillingTrialNotice() {
  const { user } = useAuth();
  const userId = user?.id;
  const role = useWindowRole();
  useEffect(() => {
    if (!userId) return;
    let disposed = false;
    let pending = false;
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;
    const seen = new Set<string>();
    const forgetDeferredTrial = () => {
      for (const key of discardDeferredTrialNotices(String(userId))) {
        seen.delete(key);
        try {
          localStorage.removeItem(key);
        } catch {
          /* Storage may be unavailable. */
        }
      }
    };
    const fetchNotice = async (afterOnboarding = false) => {
      if (disposed || pending || getWindowRoleSync() !== "owner") return;
      const authScope = getDesktopChatAuthScope();
      if (authScope && authScope.accountId !== String(userId)) return;
      pending = true;
      try {
        const sub = await getSubscriptionStatus();
        if (expiryTimer) {
          clearTimeout(expiryTimer);
          expiryTimer = null;
        }
        if (
          disposed ||
          (authScope && !isCurrentDesktopChatAuthScope(authScope))
        )
          return;
        const trialEnd = sub.trial_end
          ? new Date(sub.trial_end).getTime()
          : NaN;
        const remaining = trialEnd - Date.now();
        if (
          afterOnboarding &&
          (sub.access_status !== "trialing" ||
            !sub.trial_started_at ||
            !Number.isFinite(trialEnd) ||
            remaining <= 0 ||
            sub.subscription)
        )
          forgetDeferredTrial();
        if (
          sub.access_status === "expired" &&
          sub.trial_started_at &&
          Number.isFinite(trialEnd) &&
          remaining <= 0 &&
          !sub.has_pro_access &&
          !sub.subscription
        ) {
          if (onboardingSystemNoticesBlocked()) return;
          const key = `denker.trial-notice.${userId}.${sub.trial_end}.expired`;
          if (seen.has(key)) return;
          try {
            if (localStorage.getItem(key)) return;
          } catch {
            /* Storage may be unavailable. */
          }
          seen.add(key);
          try {
            localStorage.setItem(key, "1");
          } catch {
            /* In-memory deduplication remains available. */
          }
          showUpgradeToast(() => ({
            id: key,
            title: translate(settingsBillingMessages, "trialEndedTitle"),
            description: translate(settingsBillingMessages, "trialEndedBody", {
              price: newCheckoutAmount(),
            }),
            persistent: true,
          }));
          return;
        }
        if (
          sub.access_status !== "trialing" ||
          !sub.trial_started_at ||
          !sub.trial_end ||
          sub.subscription
        )
          return;
        if (!Number.isFinite(trialEnd) || remaining <= 0) return;
        // The minute poll handles distant deadlines. Long trial extensions can
        // overflow a browser timer's 32-bit delay and turn it into a request loop.
        if (remaining <= STATUS_REFRESH_MS)
          expiryTimer = setTimeout(() => void refresh(), remaining + 100);
        if (
          isTauri() &&
          !document.hasFocus() &&
          !onboardingSystemNoticesBlocked() &&
          !afterOnboarding
        )
          return;
        const ending = remaining <= 3 * 86400000;
        const key = `denker.trial-notice.${userId}.${sub.trial_end}.${ending ? "ending" : "started"}`;
        if (seen.has(key) && !(afterOnboarding && hasDeferredSystemNotice(key)))
          return;
        try {
          if (
            localStorage.getItem(key) &&
            !(afterOnboarding && hasDeferredSystemNotice(key))
          )
            return;
        } catch {
          /* Storage may be unavailable. */
        }
        seen.add(key);
        const titleKey = ending
          ? "trialEndsSoonTitle"
          : afterOnboarding &&
              Date.now() - Date.parse(sub.trial_started_at) < 10 * 60000
            ? "trialStartsNowTitle"
            : "trialStartedTitle";
        const trialEndDate = sub.trial_end;
        useNotificationStore.getState().addSystemToast(
          () => ({
            id: key,
            title: translate(settingsBillingMessages, titleKey),
            description: translate(
              settingsBillingMessages,
              "trialStartedBody",
              {
                date: formatDate(trialEndDate),
                price: newCheckoutAmount(),
              },
            ),
            level: "info",
            actionLabel: translate(settingsBillingMessages, "viewSubscription"),
            actionEvent: isTauri() ? "tray-settings-billing" : undefined,
          }),
          isTauri() ? undefined : openWebBillingSettings,
        );
        if (!hasDeferredSystemNotice(key)) {
          try {
            localStorage.setItem(key, "1");
          } catch {
            /* In-memory deduplication remains available. */
          }
        }
      } catch {
        if (
          afterOnboarding &&
          !disposed &&
          (!authScope || isCurrentDesktopChatAuthScope(authScope))
        )
          forgetDeferredTrial();
        // A background notice must not interrupt work when billing is unavailable.
      } finally {
        pending = false;
      }
    };
    let currentRefresh: Promise<void> | null = null;
    const refresh = (afterOnboarding = false): Promise<void> => {
      if (currentRefresh)
        return afterOnboarding
          ? currentRefresh.then(() => refresh(true))
          : currentRefresh;
      currentRefresh = fetchNotice(afterOnboarding).finally(() => {
        currentRefresh = null;
      });
      return currentRefresh;
    };
    const stopCompletionRefresh = registerOnboardingTrialRefresh(() =>
      refresh(true),
    );
    void refresh();
    const stopRefresh = watchBillingRefresh(refresh);
    const timer = setInterval(() => void refresh(), STATUS_REFRESH_MS);
    return () => {
      disposed = true;
      if (expiryTimer) clearTimeout(expiryTimer);
      clearInterval(timer);
      stopRefresh();
      stopCompletionRefresh();
    };
  }, [userId, role]);
  return null;
}
